import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { KeyRound, CheckCircle2, XCircle } from 'lucide-react';
import { bitwarden } from '../api';

/**
 * Die Zugangsdaten dieser Instanz in den Bitwarden-Tresor legen.
 *
 * Neue und geänderte Kennwörter gehen automatisch hin (wenn eingeschaltet);
 * der Knopf trägt nach — etwa für Instanzen, die älter sind als die Anbindung.
 *
 * ══ KOPIE, NICHT UMZUG ═════════════════════════════════════════════════════
 *
 * Massgeblich bleibt der PC. Was dort steht, ist verschlüsselt und wird beim
 * Ausrollen benutzt; der Bitwarden-Eintrag ist für den Fall, dass der PC
 * gerade nicht erreichbar ist. Er trägt Datum und Herkunft im Text, damit
 * niemand einer veralteten Kopie vertraut.
 *
 * ══ OHNE EINRICHTUNG UNSICHTBAR ════════════════════════════════════════════
 *
 * Ein Knopf, der nur eine Fehlermeldung erzeugt, ist schlechter als keiner.
 */
export default function BitwardenKnopf({ instanceId }: { instanceId: string }) {
  const [meldung, setMeldung] = useState<{ ok: boolean; text: string } | null>(null);

  // Die Prüfung meldet sich bei Bitwarden an — nicht bei jedem Seitenwechsel neu.
  const status = useQuery({ queryKey: ['bitwarden', 'status'], queryFn: bitwarden.status, staleTime: 5 * 60_000, retry: false });

  const schreiben = useMutation({
    mutationFn: () => bitwarden.ablegen(instanceId),
    onSuccess: (e) => setMeldung({ ok: e.ok, text: e.meldung }),
    onError: (e: Error) => setMeldung({ ok: false, text: e.message }),
  });

  if (!status.data?.eingerichtet) return null;

  return (
    <div className="rounded-lg border border-slate-200 px-3 py-2.5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="flex items-center gap-1.5 text-sm font-medium text-slate-700">
          <KeyRound size={14} /> Bitwarden
        </span>
        <button
          type="button"
          onClick={() => schreiben.mutate()}
          disabled={schreiben.isPending || status.data.ok === false}
          title={
            status.data.ok === false
              ? status.data.meldung
              : 'Legt Betreiberkonto und Kunden-Admin als Login-Einträge in der Bitwarden-Sammlung ab (oder aktualisiert sie)'
          }
          className="rounded border border-slate-300 px-2 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
        >
          {schreiben.isPending ? 'wird abgelegt …' : 'In Bitwarden ablegen'}
        </button>
      </div>

      {status.data.ok === false && (
        <p className="mt-1 text-[11px] text-amber-700">{status.data.meldung}</p>
      )}

      {meldung && (
        <p
          className={`mt-1 flex items-start gap-1 text-[11px] ${
            meldung.ok ? 'text-emerald-700' : 'text-rose-700'
          }`}
        >
          {meldung.ok ? (
            <CheckCircle2 size={11} className="mt-px shrink-0" />
          ) : (
            <XCircle size={11} className="mt-px shrink-0" />
          )}
          {meldung.text}
        </p>
      )}

      <p className="mt-1 text-[10px] leading-relaxed text-slate-500">
        {status.data.automatisch ? 'Neue und geänderte Kennwörter gehen automatisch hin. ' : ''}Kopie im Tresor —
        maßgeblich bleibt der PC; wer in Bitwarden etwas ändert, ändert an der Instanz nichts.
      </p>
    </div>
  );
}
